import { Spacing } from '@persona-id/design-tokens';
import { ReactNode, useState } from 'react';
import styled from 'styled-components';

import Form from 'dashboard/layouts/FormLayout/Form';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import TabBar, { Tab } from 'design-system/components/TabBar';

const TabContainer = styled.div<{ hidden: boolean }>`
  display: ${({ hidden }) => (hidden ? 'none' : 'block')};
  padding: ${Spacing[0]} ${Spacing[7]};
  max-height: calc(100vh - 300px);
  overflow-y: auto;

  > ${FormGroup} {
    margin-bottom: ${Spacing[3]};
  }
`;

const ModalBody = styled.section`
  flex: 1 1 auto;
  overflow-y: auto;
  margin: 0;
  padding: ${Spacing[0]};
`;

const ModalFooter = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  margin-top: auto;
  height: ${Spacing[9]};
  padding: ${Spacing[4]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
`;

interface TabbedSubmitModalProps {
  tabContents: {
    name: string;
    contents: ReactNode;
  }[];
  disableSubmit: boolean;
  isSubmitting: boolean;
  headerText: string;
  submitText: string;
  handleSubmit: () => void;
  handleCancel: () => void;
}

const TabbedSubmitModal = ({
  tabContents,
  disableSubmit,
  isSubmitting,
  headerText,
  submitText,
  handleSubmit,
  handleCancel,
}: TabbedSubmitModalProps) => {
  const [selectedTabIdx, setSelectedTabIdx] = useState(0);

  const tabsConfig = tabContents.map(({ name, contents }, idx) => ({
    name,
    contents: (
      <TabContainer key={idx} hidden={selectedTabIdx !== idx}>
        <Spacer height={2} />
        {contents}
        <Spacer height={3} />
      </TabContainer>
    ),
  }));

  return (
    <Form onSubmit={handleSubmit}>
      {/*TODO (lianga) Use PDS Modal*/}
      <ModalContainer>
        <ModalHeader onClose={handleCancel}>{headerText}</ModalHeader>
        <Spacer height={1} />
        {tabContents.length > 1 && (
          <TabBar>
            <Spacer width={3} />
            {tabsConfig.map((tabConfig, idx) => (
              <Tab
                id={`tab-${tabConfig.name}`}
                key={`tab-${tabConfig.name}`}
                selected={selectedTabIdx === idx}
                onClick={() => setSelectedTabIdx(idx)}
              >
                {tabConfig.name}
              </Tab>
            ))}
          </TabBar>
        )}
        <ModalBody>{tabsConfig.map((tabConfig) => tabConfig.contents)}</ModalBody>
        <ModalFooter>
          <HStack>
            <Button
              variant='basic'
              id='tabbed-submit-modal__cancel'
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              id='tabbed-submit-modal__submit'
              disabled={isSubmitting || disableSubmit}
              isPending={isSubmitting}
            >
              {submitText}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContainer>
    </Form>
  );
};

export default TabbedSubmitModal;
